import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BRAND, PRODUCT_SHOTS } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Authors — "Quem está por trás da licito.guru"
 * Deep navy · 3D licito glass icon floating in the background · large duotone
 * portrait bleeding out of an editorial frame · names in a bold display grid.
 */
export default function Authors() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yLogo = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const rotLogo = useTransform(scrollYProgress, [0, 1], [-6, 10]);
  const yPortrait = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="autores"
      ref={ref}
      data-testid="authors-section"
      className="relative overflow-hidden bg-[#0A1836] text-white"
    >
      {/* Ambient background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/></svg>\")",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 15% 20%, rgba(0,194,209,0.18) 0%, transparent 60%), radial-gradient(ellipse 70% 60% at 90% 85%, rgba(46,229,157,0.14) 0%, transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.25) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.35) 100%)",
        }}
      />

      {/* Glass 3D logo — corner ornament */}
      <motion.div
        aria-hidden="true"
        style={{ y: yLogo, rotate: rotLogo }}
        className="absolute -right-24 -top-16 md:-right-32 md:-top-24 w-[380px] md:w-[560px] aspect-square pointer-events-none"
      >
        <div className="absolute inset-0 rounded-full bg-[#00C2D1]/25 blur-[100px]" />
        <img
          src={BRAND.logo3D}
          alt=""
          className="relative w-full h-full object-contain opacity-60 drop-shadow-[0_20px_60px_rgba(0,194,209,0.4)]"
          style={{ filter: "blur(0.5px)" }}
        />
      </motion.div>

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8 py-24 md:py-32">
        {/* Header row */}
        <div className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
              className="font-display text-white text-[38px] sm:text-[46px] md:text-[58px] lg:text-[68px] leading-[0.98] tracking-[-0.02em] max-w-4xl"
            >
              Quem está por trás da licito.guru.
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:pb-2">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
              className="text-white/70 text-[15px] leading-relaxed max-w-sm"
            >
              Duas das maiores referências do país em licitações e contratos
              públicos uniram conhecimento à inteligência artificial.
            </motion.p>
          </div>
        </div>

        {/* Portrait + authors grid */}
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-stretch">
          {/* Portrait */}
          <motion.div
            style={{ y: yPortrait }}
            className="col-span-12 lg:col-span-7 relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#00C2D1]/20 to-[#2EE59D]/20 rounded-lg blur-2xl opacity-70" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className="relative rounded-lg overflow-hidden border border-white/10"
              style={{
                boxShadow:
                  "0 40px 80px -20px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.06)",
              }}
            >
              <img
                src={PRODUCT_SHOTS.founders}
                alt="Joel de Menezes Niebuhr e Gustavo Schiefler"
                className="w-full h-auto block"
                loading="lazy"
              />
              {/* subtle duotone overlay to blend with the deep navy */}
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none mix-blend-multiply"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(26,58,97,0) 40%, rgba(26,58,97,0.55) 100%)",
                }}
              />
            </motion.div>
          </motion.div>

          {/* Authors list */}
          <div className="col-span-12 lg:col-span-5 flex flex-col justify-center gap-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
              className="border-l-2 border-[#00C2D1] pl-6 md:pl-8"
            >
              <h3 className="font-display text-white text-3xl md:text-4xl leading-[1.05] tracking-[-0.015em]">
                Joel de Menezes Niebuhr
              </h3>
              <p className="mt-3 text-white/75 text-[14.5px] leading-[1.6]">
                Doutor em Direito do Estado pela PUC/SP. Mestre em Direito pela
                UFSC. Professor em cursos de capacitação em licitações públicas
                há mais de 20 anos. Autor das obras que fundamentam o banco de
                dados jurídicos da licito.guru IA.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
              className="border-l-2 border-[#2EE59D] pl-6 md:pl-8"
            >
              <h3 className="font-display text-white text-3xl md:text-4xl leading-[1.05] tracking-[-0.015em]">
                Gustavo Schiefler
              </h3>
              <p className="mt-3 text-white/75 text-[14.5px] leading-[1.6]">
                Doutor em Direito do Estado (USP). Mestre e graduado em Direito
                (UFSC). Foi pesquisador visitante no Max-Planck-Institut, em
                Hamburgo. Educação Executiva pela Harvard Law School (Program on
                Negotiation). Professor em cursos de capacitação em licitações
                públicas há mais de 10 anos. Professor da pós-graduação
                (mestrado e especialização) do IDP em Brasília.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
